// ABOUTME: Core TypeScript type definitions for Obsidian BI frontend.
// ABOUTME: Includes app state types, investigation types, report types, and insight types.

export enum AppMode {
  Home = 'HOME',
  Investigation = 'INVESTIGATION',
  Report = 'REPORT',
}

export enum InvestigationState {
  Idle = 'IDLE',
  Input = 'INPUT',
  Thinking = 'THINKING',
  Result = 'RESULT',
}

export interface DataPoint {
  label: string;
  value: number;
  prev?: number;
  group?: string;
}

export interface Insight {
  id: string;
  title: string;
  value: string;
  change: string;
  trend: 'up' | 'down' | 'neutral';
  isNegative?: boolean;
  history: DataPoint[];
  context?: string;
  investigationPrompt?: string;
}

export interface InvestigationResult {
  title: string;
  subtitle: string;
  chartData: DataPoint[];
  chartType: 'bar' | 'line' | 'area' | 'scatter';
  chartTitle?: string;
  summary: string;
  details: string;
  suggestedQuestions?: string[];
}

export interface TraceStep {
  id: string;
  label: string;
  status: 'pending' | 'active' | 'complete';
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  result?: InvestigationResult;
  timestamp: Date;
}

export interface InvestigationSession {
  id: string;
  title: string;
  lastActive: Date;
  lastActiveRaw: string; // Raw ISO string from API for display
  messages: ChatMessage[];
  messageCount?: number;
  dataSourceIds?: string[];
  lastEditedAt?: Date | null;
  lastEditedBy?: { id: string; name: string; email: string } | null;
}

export type ReportBlockType = 'h1' | 'h2' | 'paragraph' | 'insight';

export interface ReportBlock {
  id: string;
  type: ReportBlockType;
  content: string;
  data?: unknown;
}

export interface InsightSelectionReasoning {
  primaryReason: string;
  dataCharacteristics: string[];
  relevanceScore: number;
  alternativesConsidered?: Array<{
    type: InsightType;
    reason: string;
  }>;
  confidence: 'high' | 'medium' | 'low';
  assumptions?: string[];
}

export interface SmartAction {
  id: string;
  label: string;
  icon: 'filter' | 'export' | 'share' | 'alert' | 'drill-down' | 'compare' | 'view' | 'create' | 'ask';
  actionType: 'navigate' | 'query' | 'export' | 'modal' | 'external';
  queryPrompt?: string;
  navigationPath?: string;
  exportFormat?: 'csv' | 'pdf' | 'png';
  modalId?: string;
}

export interface TableColumn {
  id: string;
  header: string;
  accessor: string;
  type: 'text' | 'number' | 'currency' | 'percentage' | 'status' | 'date';
  align?: 'left' | 'center' | 'right';
  format?: {
    currency?: string;
    decimals?: number;
    showSign?: boolean;
    dateFormat?: string;
  };
  statusColors?: Record<string, 'success' | 'warning' | 'danger' | 'neutral'>;
  sortable?: boolean;
  width?: string;
}

export interface TableRow {
  id: string;
  [key: string]: string | number | boolean | null | undefined;
}

export interface InsightTable {
  title?: string;
  columns: TableColumn[];
  rows: TableRow[];
  maxDisplayRows?: number;
  showRowNumbers?: boolean;
  summaryRow?: TableRow;
}

export interface InsightChart {
  title?: string;
  type: 'bar' | 'line' | 'area' | 'pie' | 'stacked-bar' | 'grouped-bar';
  data: DataPoint[];
  xAxis?: {
    label?: string;
    type?: 'category' | 'time' | 'value';
    dateFormat?: string;
  };
  yAxis?: {
    label?: string;
    min?: number;
    max?: number;
    format?: 'number' | 'currency' | 'percentage';
    currency?: string;
  };
  legend?: {
    show: boolean;
    position?: 'top' | 'bottom' | 'left' | 'right';
    items?: Array<{ label: string; color: string }>;
  };
  colorScheme?: 'default' | 'sequential' | 'diverging' | 'categorical';
  colors?: string[];
  referenceLines?: Array<{
    value: number;
    label: string;
    style: 'solid' | 'dashed';
    color?: string;
  }>;
  showDataLabels?: boolean;
  aspectRatio?: number;
}

export type MetricSentiment = 'positive' | 'negative' | 'warning' | 'critical' | 'neutral';

export interface KeyMetric {
  label: string;
  value: string | number;
  displayValue: string;
  sentiment: MetricSentiment;
  sentimentReason?: string;
  change?: {
    value: number;
    direction: 'up' | 'down' | 'neutral';
    sentiment: MetricSentiment;
    periodLabel?: string;
  };
  context?: string;
  threshold?: {
    type: 'above' | 'below' | 'between';
    value: number;
    upperValue?: number;
    breached: boolean;
    description?: string;
  };
}

export interface SummaryBulletPoint {
  text: string;
  subPoints?: string[];
  emphasis?: 'normal' | 'highlight' | 'critical';
}

export interface InsightSummary {
  introduction?: string;
  bulletPoints: SummaryBulletPoint[];
  conclusion?: string;
}

export type InsightType =
  | 'table'
  | 'chart'
  | 'summary'
  | 'table-summary'
  | 'chart-table'
  | 'metric-card'
  | 'python-viz'
  | 'python-viz-with-summary';

export interface BaseInsight {
  id: string;
  title: string;
  type: InsightType;
  // Single paragraph describing the full insight - what it shows, key findings, etc.
  insightOverview?: string;
  selectionReasoning: InsightSelectionReasoning;
  smartActions: SmartAction[];
  generatedAt: Date;
  sourceQuery?: string;
  dataSourceId?: string;
  pinned?: boolean;
}

export interface TableInsight extends BaseInsight {
  type: 'table';
  table: InsightTable;
  description?: string;
}

export interface ChartInsight extends BaseInsight {
  type: 'chart';
  chart: InsightChart;
  summary?: InsightSummary;
  keyMetrics?: KeyMetric[];
}

export interface SummaryInsight extends BaseInsight {
  type: 'summary';
  summary: InsightSummary;
  keyMetrics: KeyMetric[];
}

export interface TableSummaryInsight extends BaseInsight {
  type: 'table-summary';
  table: InsightTable;
  summary: InsightSummary;
}

export interface ChartTableInsight extends BaseInsight {
  type: 'chart-table';
  chart: InsightChart;
  table: InsightTable;
  layout: 'side-by-side' | 'stacked';
}

export interface MetricCardInsight extends BaseInsight {
  type: 'metric-card';
  metric: KeyMetric;
  sparklineData?: DataPoint[];
  alertThreshold?: {
    type: 'above' | 'below';
    value: number;
    triggered: boolean;
  };
}

export type InsightVariant =
  | TableInsight
  | ChartInsight
  | SummaryInsight
  | TableSummaryInsight
  | ChartTableInsight
  | MetricCardInsight;

export interface TypedInvestigationResult {
  id: string;
  title: string;
  subtitle: string;
  insight: InsightVariant;
  suggestedQuestions: string[];
  generatedAt: Date;
}
